import { useMemo, VFC } from 'react';

import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import ReactSelect from 'dashboard/components/ReactSelect';
import { StepAdvancedConfigurationWithErrorHandlingPanel } from 'dashboard/components/WorkflowControlPanel/StepAdvancedConfigurationWithErrorHandlingPanel';
import { ParamsBaseProps } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsBaseProps';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { filterUsableObjects, UsableObjectConfig } from 'dashboard/lib/workflows';
import { Trigger } from 'dashboard/models/trigger';
import { WorkflowError, WorkflowFieldObjectKey } from 'dashboard/models/workflow';
import { WorkflowActionCreateSARV1 } from 'dashboard/models/workflow-version';
import Spacer from 'design-system/components/Spacer';

interface ParamsCreateSARV1Props extends ParamsBaseProps {
  editable: boolean;
  errors: WorkflowError[];
  params: WorkflowActionCreateSARV1['params'];
  trigger?: Trigger;
  usableObjects: UsableObjectConfig[];
  updateActionParams: (newParams: WorkflowActionCreateSARV1['params']) => void;
}

const ParamsCreateSARV1: VFC<ParamsCreateSARV1Props> = ({
  step,
  editable,
  errors,
  params,
  trigger,
  usableObjects,
  updateActionParams,
  updateStep,
}) => {
  const selectSARObject = (values: string[]) => {
    updateActionParams({
      ...params,
      'sar-objects': values,
    });
  };

  const possibleSARObjects = useMemo(() => {
    return filterUsableObjects({
      usableObjects,
      options: {
        permittedObjects: [WorkflowFieldObjectKey.Account, WorkflowFieldObjectKey.Transaction],
        excludeNonPersonaObjects: true,
      },
    }).map(({ value, label }) => ({ value, label }));
  }, [usableObjects]);

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionCreateSARV1['params']>(errors);

  return (
    <>
      <Spacer height={4} />
      <StepAdvancedConfigurationWithErrorHandlingPanel
        step={step}
        trigger={trigger}
        updateStep={updateStep}
        disabled={!editable}
      >
        <Spacer height={2} />
        <FormControlLabel htmlFor='params-create-sar__sar-objects-input'>
          SAR Objects
        </FormControlLabel>
        <ReactSelect
          value={params['sar-objects']}
          isMulti
          options={possibleSARObjects}
          onChange={selectSARObject}
          id='params-create-sar__sar-objects'
          disabled={!editable}
          hasError={hasErrorFor('sar-objects')}
        />
        {renderErrorMessageFor('sar-objects')}
        <Spacer height={2} />
      </StepAdvancedConfigurationWithErrorHandlingPanel>
    </>
  );
};

export default ParamsCreateSARV1;
